import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { usePageMeta } from '../hooks/usePageMeta';
import ScrollToTop from '../components/ScrollToTop';
import Reveal from '../components/Reveal';
import Hero from '../components/Hero';

const Privacy = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage].privacyPolicy;
  usePageMeta({ title: t.title, description: t.introduction, path: 'privacy' });

  // Helper function to render formatted text with markdown bold and line breaks
  const renderFormattedText = (text) => {
    const paragraphs = text.split('\n\n');
    return paragraphs.map((paragraph, index) => {
      const parts = paragraph.split(/(\*\*.*?\*\*)/g);
      const formattedParts = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      return (
        <p key={index} className="text-stone-700 leading-relaxed mb-4 last:mb-0">
          {formattedParts}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Hero
        gradientClass="hero-legal-privacy"
        title={t.title}
        description={`${t.lastUpdated}: 01.01.2026`}
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <Reveal>
          <p className="text-lg text-stone-700 mb-12 leading-relaxed">
            {t.introduction}
          </p>
        </Reveal>

        {/* Sections */}
        <div className="space-y-12">
          {/* Responsible */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.responsible.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.responsible.content}
            </p>
          </Reveal>

          {/* Data Collection */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.dataCollection.title}
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              {t.sections.dataCollection.intro}
            </p>
            <ul className="space-y-3">
              <li className="text-stone-700 leading-relaxed">
                <strong>{currentLanguage === 'de' ? 'Kontaktformulare:' : currentLanguage === 'en' ? 'Contact Forms:' : currentLanguage === 'sv' ? 'Kontaktformulär:' : 'Kapcsolati űrlapok:'}</strong> {t.sections.dataCollection.contact}
              </li>
              <li className="text-stone-700 leading-relaxed">
                <strong>{currentLanguage === 'de' ? 'Website-Analytik:' : currentLanguage === 'en' ? 'Website Analytics:' : currentLanguage === 'sv' ? 'Webbplatsanalys:' : 'Weboldal-analitika:'}</strong> {t.sections.dataCollection.analytics}
              </li>
            </ul>
          </Reveal>

          {/* Purposes */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.purposes.title}
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              {t.sections.purposes.intro}
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-700">
              {t.sections.purposes.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </Reveal>

          {/* Legal Basis */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.legalBasis.title}
            </h2>
            <div>
              {renderFormattedText(t.sections.legalBasis.content)}
            </div>
          </Reveal>

          {/* Data Sharing */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.dataSharing.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.dataSharing.content}
            </p>
          </Reveal>

          {/* Storage */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.storage.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.storage.content}
            </p>
          </Reveal>

          {/* Rights */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.rights.title}
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              {t.sections.rights.intro}
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-700">
              {t.sections.rights.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </Reveal>

          {/* Contact */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.contact.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.contact.content}
            </p>
          </Reveal>

          {/* Cookies */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.cookies.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.cookies.content}
            </p>
          </Reveal>

          {/* Security */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.security.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.security.content}
            </p>
          </Reveal>

          {/* Third Parties */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.thirdParties.title}
            </h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              {t.sections.thirdParties.intro}
            </p>
            <ul className="list-disc list-inside space-y-2 text-stone-700">
              <li><strong>Umami Analytics:</strong> {t.sections.thirdParties.umami}</li>
              <li><strong>StaticForms:</strong> {t.sections.thirdParties.staticforms}</li>
            </ul>
          </Reveal>

          {/* Changes */}
          <Reveal as="section">
            <h2 className="font-serif text-2xl font-bold text-stone-900 mb-4 pb-3 border-b-2 border-amber-600">
              {t.sections.changes.title}
            </h2>
            <p className="text-stone-700 leading-relaxed">
              {t.sections.changes.content}
            </p>
          </Reveal>
        </div>

        {/* Footer CTA */}
        <Reveal className="mt-16 p-8 bg-amber-50 border-l-4 border-amber-600 rounded-sm">
          <p className="text-stone-700 text-lg">
            {currentLanguage === 'de'
              ? 'Haben Sie noch Fragen zu dieser Datenschutzerklärung? Kontaktieren Sie uns gerne!'
              : currentLanguage === 'en'
              ? 'Do you have any further questions about this privacy policy? Feel free to contact us!'
              : currentLanguage === 'sv'
              ? 'Har du fler frågor om denna integritetspolicy? Kontakta oss gärna!'
              : 'Még kérdések az adatvédelmi nyilatkozattal kapcsolatban? Vegye fel velünk a kapcsolatot!'}
          </p>
        </Reveal>
      </div>
      <ScrollToTop />
    </div>
  );
};

export default Privacy;
